import { getTracerProxy, TracerEventType } from './trace.js';
import { dirtySignal } from './dirty.js';
import { getCurrentConsumer } from './consumer.js';
import { scheduleListeners } from './scheduling.js';
import { FALSE_EQUALS } from './utils/equals.js';
let STATE_ID = 0;
const SIGNAL_BRAND = Symbol.for('signalium.signal');
export function isSignal(value) {
    return typeof value === 'object' && value !== null && value[SIGNAL_BRAND] === true;
}
export class StateSignal {
    [SIGNAL_BRAND] = true;
    _value;
    _equals;
    _subs = new Map();
    _desc;
    _id;
    _listeners = null;
    constructor(value, equals = (a, b) => a === b, desc = 'signal') {
        this._value = value;
        this._equals = equals;
        if (IS_DEV) {
            this._id = STATE_ID++;
            this._desc = desc;
        }
    }
    get value() {
        this.consume();
        return this._value;
    }
    update(fn) {
        this.value = fn(this._value);
    }
    set value(value) {
        if (this._equals(value, this._value)) {
            return;
        }
        this._value = value;
        this.notify();
    }
    consume() {
        const currentConsumer = getCurrentConsumer();
        if (currentConsumer !== undefined) {
            if (IS_DEV) {
                const tracer = getTracerProxy();
                tracer?.emit({
                    type: TracerEventType.ConsumeState,
                    id: currentConsumer.tracerMeta.id,
                    name: this._desc,
                    childId: this._id,
                    value: this._value,
                    setValue: (value) => {
                        this.value = value;
                    },
                });
            }
            this._subs.set(currentConsumer.ref, currentConsumer.computedCount);
        }
    }
    notify() {
        const { _subs: subs } = this;
        for (const [subRef, consumedAt] of subs.entries()) {
            const sub = subRef.deref();
            if (sub === undefined || consumedAt !== sub.computedCount) {
                continue;
            }
            dirtySignal(sub);
        }
        this._subs.clear();
        scheduleListeners(this);
    }
    addListener(listener) {
        let listeners = this._listeners;
        if (listeners === null) {
            this._listeners = listeners = new Set();
        }
        listeners.add(listener);
        return () => listeners.delete(listener);
    }
}
export function runListeners(signal) {
    const listeners = signal['_listeners'];
    if (listeners === null) {
        return;
    }
    for (const listener of listeners) {
        listener();
    }
}
export function signal(initialValue, opts) {
    const equals = opts?.equals === false ? FALSE_EQUALS : (opts?.equals ?? ((a, b) => a === b));
    return new StateSignal(initialValue, equals, opts?.desc);
}
export const notifier = (opts) => {
    return new StateSignal(undefined, FALSE_EQUALS, opts?.desc);
};
